import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Typography from "../components/v2/Typography";
import { ChevronUp } from "lucide-react";
import ContactSection from "../atoms/Contact";
import Footer from "../atoms/Footer";

const PrivacyPage = () => {
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300);
            
            // Update active section based on scroll position
            const sections = document.querySelectorAll('.privacy-section');
            sections.forEach(section => {
                const rect = section.getBoundingClientRect();
                if (rect.top <= 100 && rect.bottom >= 100) {
                    setActiveSection(section.id);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <div className="privacy-page">
                <div className="privacy-container">
                    <div className="privacy-header" style={{ paddingTop: '80px' }}>
                        <Typography variant="h1">Privacy Policy</Typography>
                        <p className="effective-date">Effective Date: January 1, 2025</p>
                    </div>

                    <div className="privacy-nav">
                        <div className="nav-content">
                            <h4>Quick Navigation</h4>
                            <ul>
                                <li><a href="#introduction" className={activeSection === 'introduction' ? 'active' : ''}>Introduction</a></li>
                                <li><a href="#information-collect" className={activeSection === 'information-collect' ? 'active' : ''}>Information We Collect</a></li>
                                <li><a href="#how-we-use" className={activeSection === 'how-we-use' ? 'active' : ''}>How We Use Information</a></li>
                                <li><a href="#sharing" className={activeSection === 'sharing' ? 'active' : ''}>Information Sharing</a></li>
                                <li><a href="#security" className={activeSection === 'security' ? 'active' : ''}>Data Security</a></li>
                                <li><a href="#your-rights" className={activeSection === 'your-rights' ? 'active' : ''}>Your Rights</a></li>
                                <li><a href="#cookies" className={activeSection === 'cookies' ? 'active' : ''}>Cookies Policy</a></li>
                                <li><a href="#contact" className={activeSection === 'contact' ? 'active' : ''}>Contact Us</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-content">
                        <div className="quick-links">
                            <h3>Privacy at a Glance</h3>
                            <div className="links-grid">
                                <a href="#information-collect">What We Collect</a>
                                <a href="#how-we-use">How We Use Data</a>
                                <a href="#your-rights">Your Privacy Rights</a>
                                <a href="#security">Security Measures</a>
                            </div>
                        </div>

                        <div id="introduction" className="privacy-section">
                            <h2>1. Introduction</h2>
                            <p>Welcome to NameConnect's Privacy Policy. At NameConnect, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our domain brokerage and related services.</p>
                            
                            <div className="info-box">
                                <p><strong>Our Commitment:</strong> We believe in transparency and giving you control over your personal data. This policy outlines our practices and your rights regarding your information.</p>
                            </div>

                            <p>By using our services, you consent to the data practices described in this policy. If you do not agree with our practices, please do not use our services.</p>
                        </div>

                        <div id="information-collect" className="privacy-section">
                            <h2>2. Information We Collect</h2>
                            <p>We collect information to provide better services to our users. The types of information we collect include:</p>

                            <h3>2.1 Information You Provide Directly</h3>
                            <ul>
                                <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account</li>
                                <li><strong>Business Information:</strong> Company name, business address, and tax identification numbers</li>
                                <li><strong>Domain Information:</strong> Details about domains you own, wish to buy, or sell</li>
                                <li><strong>Communication Data:</strong> Messages, inquiries, and feedback you send to us</li>
                                <li><strong>Payment Information:</strong> Billing address and payment method details (processed securely through third-party providers)</li>
                            </ul>

                            <h3>2.2 Information Collected Automatically</h3>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Type of Data</th>
                                        <th>Examples</th>
                                        <th>Purpose</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Device Information</td>
                                        <td>IP address, browser type, operating system</td>
                                        <td>Security and optimization</td>
                                    </tr>
                                    <tr>
                                        <td>Usage Data</td>
                                        <td>Pages visited, features used, time spent</td>
                                        <td>Service improvement</td>
                                    </tr>
                                    <tr>
                                        <td>Location Data</td>
                                        <td>Country, region (from IP address)</td>
                                        <td>Compliance and localization</td>
                                    </tr>
                                    <tr>
                                        <td>Cookies</td>
                                        <td>Session cookies, preference cookies</td>
                                        <td>User experience enhancement</td>
                                    </tr>
                                </tbody>
                            </table>

                            <h3>2.3 Information from Third Parties</h3>
                            <ul>
                                <li>Public WHOIS database information</li>
                                <li>Domain registration history from public sources</li>
                                <li>Business verification information from trusted providers</li>
                                <li>Social media profiles (if you connect them)</li>
                            </ul>
                        </div>

                        <div id="how-we-use" className="privacy-section">
                            <h2>3. How We Use Your Information</h2>
                            <p>We use the information we collect for the following purposes:</p>

                            <h3>3.1 Service Delivery</h3>
                            <ul>
                                <li>Process domain transactions and facilitate negotiations</li>
                                <li>Provide domain valuation and consulting services</li>
                                <li>Manage your account and authenticate your identity</li>
                                <li>Communicate about transactions and service updates</li>
                                <li>Process payments and prevent fraud</li>
                            </ul>

                            <h3>3.2 Service Improvement</h3>
                            <ul>
                                <li>Analyze usage patterns to improve our platform</li>
                                <li>Develop new features and services</li>
                                <li>Personalize your experience based on preferences</li>
                                <li>Conduct research and analytics</li>
                            </ul>

                            <h3>3.3 Marketing and Communication</h3>
                            <ul>
                                <li>Send service-related announcements and updates</li>
                                <li>Provide marketing communications (with your consent)</li>
                                <li>Respond to your inquiries and support requests</li>
                                <li>Send transaction confirmations and receipts</li>
                            </ul>

                            <h3>3.4 Legal and Security</h3>
                            <ul>
                                <li>Comply with legal obligations and regulations</li>
                                <li>Protect against fraud and unauthorized transactions</li>
                                <li>Enforce our terms of service and policies</li>
                                <li>Respond to legal requests and prevent harm</li>
                            </ul>
                        </div>

                        <div id="sharing" className="privacy-section">
                            <h2>4. Information Sharing and Disclosure</h2>
                            <p>We value your privacy and do not sell your personal information. We may share your information in the following circumstances:</p>

                            <h3>4.1 With Your Consent</h3>
                            <p>We share information when you give us explicit permission, such as when you authorize us to contact domain owners on your behalf.</p>

                            <h3>4.2 Service Providers</h3>
                            <p>We work with trusted third-party service providers who assist us in operating our business:</p>
                            <ul>
                                <li>Payment processors (e.g., Stripe, PayPal)</li>
                                <li>Escrow services (e.g., Escrow.com)</li>
                                <li>Cloud hosting providers</li>
                                <li>Email service providers</li>
                                <li>Analytics services</li>
                            </ul>

                            <div className="info-box">
                                <p><strong>Note:</strong> All service providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>
                            </div>

                            <h3>4.3 Business Transactions</h3>
                            <p>In connection with domain transactions, we may share limited information with:</p>
                            <ul>
                                <li>Domain buyers or sellers (with consent)</li>
                                <li>Domain registrars for transfer purposes</li>
                                <li>Escrow services for secure transactions</li>
                            </ul>

                            <h3>4.4 Legal Requirements</h3>
                            <p>We may disclose information if required by law or in response to valid legal requests, including:</p>
                            <ul>
                                <li>Court orders or subpoenas</li>
                                <li>Government investigations</li>
                                <li>To protect our rights or property</li>
                                <li>In emergencies to protect personal safety</li>
                            </ul>
                        </div>

                        <div id="security" className="privacy-section">
                            <h2>5. Data Security</h2>
                            <p>We implement robust security measures to protect your personal information:</p>

                            <h3>5.1 Technical Safeguards</h3>
                            <ul>
                                <li><strong>Encryption:</strong> SSL/TLS encryption for data in transit</li>
                                <li><strong>Secure Storage:</strong> Encrypted databases and secure servers</li>
                                <li><strong>Access Controls:</strong> Role-based access restrictions</li>
                                <li><strong>Monitoring:</strong> 24/7 security monitoring and intrusion detection</li>
                                <li><strong>Regular Updates:</strong> Timely security patches and updates</li>
                            </ul>

                            <h3>5.2 Organizational Measures</h3>
                            <ul>
                                <li>Employee training on data protection</li>
                                <li>Confidentiality agreements with staff</li>
                                <li>Regular security audits and assessments</li>
                                <li>Incident response procedures</li>
                                <li>Data minimization practices</li>
                            </ul>

                            <div className="info-box">
                                <p><strong>Your Role:</strong> Help us protect your account by using strong passwords, enabling two-factor authentication, and reporting suspicious activity immediately.</p>
                            </div>
                        </div>

                        <div id="your-rights" className="privacy-section">
                            <h2>6. Your Privacy Rights</h2>
                            <p>You have several rights regarding your personal information:</p>

                            <h3>6.1 Access and Portability</h3>
                            <ul>
                                <li>Request a copy of your personal data</li>
                                <li>Receive your data in a portable format</li>
                                <li>Access information about how we use your data</li>
                            </ul>

                            <h3>6.2 Correction and Deletion</h3>
                            <ul>
                                <li>Update or correct inaccurate information</li>
                                <li>Request deletion of your personal data</li>
                                <li>Remove your account and associated data</li>
                            </ul>

                            <h3>6.3 Control and Consent</h3>
                            <ul>
                                <li>Opt-out of marketing communications</li>
                                <li>Manage cookie preferences</li>
                                <li>Withdraw consent for data processing</li>
                                <li>Object to certain uses of your data</li>
                            </ul>

                            <h3>6.4 How to Exercise Your Rights</h3>
                            <p>To exercise any of these rights, please contact us using the information provided below. We will respond to your request within 30 days.</p>
                        </div>

                        <div id="cookies" className="privacy-section">
                            <h2>7. Cookies and Tracking Technologies</h2>
                            <p>We use cookies and similar technologies to enhance your experience on our platform.</p>

                            <h3>7.1 Types of Cookies We Use</h3>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Cookie Type</th>
                                        <th>Purpose</th>
                                        <th>Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Essential Cookies</td>
                                        <td>Enable basic site functionality</td>
                                        <td>Session</td>
                                    </tr>
                                    <tr>
                                        <td>Analytics Cookies</td>
                                        <td>Help us understand site usage</td>
                                        <td>2 years</td>
                                    </tr>
                                    <tr>
                                        <td>Preference Cookies</td>
                                        <td>Remember your settings and preferences</td>
                                        <td>1 year</td>
                                    </tr>
                                    <tr>
                                        <td>Marketing Cookies</td>
                                        <td>Deliver relevant advertisements</td>
                                        <td>90 days</td>
                                    </tr>
                                </tbody>
                            </table>

                            <h3>7.2 Managing Cookies</h3>
                            <p>You can control cookies through your browser settings. However, disabling certain cookies may limit your ability to use some features of our service.</p>
                        </div>

                        <div id="international" className="privacy-section">
                            <h2>8. International Data Transfers</h2>
                            <p>As a global service, we may transfer your information across borders:</p>
                            <ul>
                                <li>We use appropriate safeguards for international transfers</li>
                                <li>Data is processed in accordance with this privacy policy</li>
                                <li>We comply with applicable data protection laws</li>
                                <li>You can request information about transfer mechanisms</li>
                            </ul>
                        </div>

                        <div id="children" className="privacy-section">
                            <h2>9. Children's Privacy</h2>
                            <p>Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.</p>
                        </div>

                        <div id="changes" className="privacy-section">
                            <h2>10. Changes to This Policy</h2>
                            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
                            <ul>
                                <li>Posting the new policy on our website</li>
                                <li>Updating the "Effective Date" at the top</li>
                                <li>Sending an email notification for significant changes</li>
                                <li>Requiring consent where legally required</li>
                            </ul>
                        </div>

                        <div id="contact" className="privacy-section">
                            <h2>11. Contact Us</h2>
                            <div className="contact-section">
                                <h3>Privacy Questions or Concerns?</h3>
                                <p>If you have any questions about this Privacy Policy or our data practices, please contact our Privacy Team:</p>
                                <p><strong>Email:</strong> <a href="mailto:info@nameconnect.com">info@nameconnect.com</a></p>
                                <p><strong>Phone:</strong> +1 (941) 500-9689</p>
                                <p><strong>Address:</strong> 555 Grove Street, Herndon VA, 20170</p>
                                <p>We take all privacy concerns seriously and will respond promptly to your inquiries.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <button 
                    className={`scroll-to-top ${showScrollTop ? 'visible' : ''}`}
                    onClick={scrollToTop}
                    aria-label="Scroll to top"
                >
                    <ChevronUp size={24} />
                </button>
            </div>

            <ContactSection />
        </>
    );
};

export default PrivacyPage;